// =============================================================================
// staticSite/urlPolicy — JEDNA zasada: adresy na stronie statycznej sa IDENTYCZNE
// jak na WordPressie. Jesli strona ma URL WP (wp_post_url / wp_page_url / link
// z REST), jego sciezka wygrywa dosłownie. Dopiero bez URL-a liczymy ze sluga.
//
// Czyste funkcje — bez DB, bez DOM. Testowane w vitest.
// =============================================================================

import { slugify } from "@/lib/slugify";

/**
 * Kopia logiki buildCanonicalUrl z schemaGenerator (celowo bez importu — tamten
 * modul ciagnie klienta Supabase, a urlPolicy ma byc czysty i testowalny).
 * Zawsze trailing slash, brak `//`.
 */
export function buildCanonicalUrl(siteBase: string, slug: string): string {
  const base = siteBase.endsWith("/") ? siteBase.slice(0, -1) : siteBase;
  const stripped = (slug || "").replace(/^\/+/, "").replace(/\/+$/, "");
  if (!stripped) return base + "/";
  return `${base}/${stripped}/`;
}

/** "https://www.Przyklad.pl/" -> "przyklad.pl" */
export function domainFromUrl(url: string): string {
  const raw = (url || "").trim();
  if (!raw) return "";
  try {
    const host = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname.toLowerCase();
    return host.replace(/^www\./, "");
  } catch {
    return raw.toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  }
}

/** "https://przyklad.pl" — apex, https, bez trailing slash. */
export function baseUrlForDomain(domain: string): string {
  return `https://${domain.replace(/^www\./, "")}`;
}

/** Host z URL-a (lowercase) albo "" gdy URL wzgledny / niepoprawny. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

/** Czy host nalezy do domeny strony (apex lub www). */
export function isSiteHost(host: string, domain: string): boolean {
  const h = host.toLowerCase();
  const d = domain.toLowerCase();
  return h === d || h === `www.${d}`;
}

/**
 * Normalizuje sciezke do formatu strony statycznej:
 *  - leading slash, trailing slash (katalog = index.html)
 *  - bez query / hash
 *  - podwojne slashe zlozone
 *  - percent-encoding zdekodowany (polskie znaki jako znaki, nie %C5%82)
 *  - sciezki z rozszerzeniem pliku (.xml, .txt, .html, .jpg...) bez trailing slash
 */
export function normalizePath(input: string): string {
  let p = (input || "").trim();
  const q = p.search(/[?#]/);
  if (q >= 0) p = p.slice(0, q);
  try {
    p = decodeURIComponent(p);
  } catch {
    // zostaw jak jest
  }
  p = p.replace(/\/{2,}/g, "/");
  if (!p.startsWith("/")) p = `/${p}`;
  const last = p.split("/").pop() || "";
  const looksLikeFile = /\.[a-z0-9]{1,8}$/i.test(last);
  if (!looksLikeFile && !p.endsWith("/")) p = `${p}/`;
  return p;
}

/** Sciezka z pelnego URL-a (albo z samej sciezki). */
export function pathFromUrl(url: string): string {
  const raw = (url || "").trim();
  if (!raw) return "/";
  try {
    const u = new URL(raw.includes("://") ? raw : `https://x.invalid${raw.startsWith("/") ? "" : "/"}${raw}`);
    return normalizePath(u.pathname);
  } catch {
    return normalizePath(raw);
  }
}

/** Sciezka artykulu ze sluga — ta sama logika co canonical w Schema.org (buildCanonicalUrl). */
export function pathForSlug(baseUrl: string, slug: string): string {
  return pathFromUrl(buildCanonicalUrl(baseUrl, slug));
}

/** Sciezka kategorii: /category/<slug>/ (baza konfigurowalna per domena). */
export function categoryPath(slug: string, categoryBase = "category"): string {
  // "" = kategorie bez bazy (/slug/); undefined = domyslne "category"
  const base = (categoryBase ?? "category").replace(/^\/+|\/+$/g, "");
  const s = (slug || "").replace(/^\/+|\/+$/g, "");
  if (!s) return `/${base}/`;
  return base ? `/${base}/${s}/` : `/${s}/`;
}

/** Sciezka podstrony: URL WP wygrywa, inaczej slug z tytulu. */
export function subpagePath(input: { wp_page_url?: string | null; title: string }): string {
  if (input.wp_page_url) return pathFromUrl(input.wp_page_url);
  return `/${slugify(input.title)}/`;
}

/** Sciezka strony autora: link z WP REST wygrywa, inaczej slug autora / etykiety. */
export function authorPath(input: {
  wpLink?: string | null;
  author_slug?: string | null;
  public_author_label?: string | null;
}): string | null {
  if (input.wpLink) return pathFromUrl(input.wpLink);
  const slug = (input.author_slug || "").trim() || slugify(input.public_author_label || "");
  return slug ? `/${slug}/` : null;
}

/** Sciezka pliku w projekcie Pages dla sciezki URL: "/slug/" -> "/slug/index.html". */
export function filePathForUrlPath(urlPath: string): string {
  const p = normalizePath(urlPath);
  return p.endsWith("/") ? `${p}index.html` : p;
}

/** Sciezka -> pelny URL kanoniczny. */
export function absoluteUrl(baseUrl: string, path: string): string {
  const base = baseUrl.replace(/\/$/, "");
  return `${base}${normalizePath(path)}`;
}

/** Sciezka bezpieczna do sitemap/hrefow: percent-encoding tylko tam, gdzie trzeba. */
export function encodePath(path: string): string {
  return path
    .split("/")
    .map((seg) => encodeURIComponent(seg).replace(/%2F/gi, "/"))
    .join("/");
}
